import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { CURRENCIES, detectCurrency, fetchRates, formatGBPInCurrency, parseGBP, convertAndFormat } from "@/lib/currency";
import { useSettings } from "@/context/SettingsContext";

const STORAGE_KEY = "demj_currency_v1";
const DEFAULT_RATES = { GBP: 1, EUR: 1.17, USD: 1.27, CAD: 1.73, NGN: 1900 };

const CurrencyContext = createContext({
  currency: "GBP",
  rates: DEFAULT_RATES,
  setCurrency: () => {},
  formatPrice: (s) => s,
  convertAmount: (n) => `£${n}`,
});

export function CurrencyProvider({ children }) {
  const { settings } = useSettings();
  const [currency, setCurrencyState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || "GBP";
    } catch {
      return "GBP";
    }
  });
  const [liveRates, setLiveRates] = useState(DEFAULT_RATES);
  const [manuallySet, setManuallySet] = useState(() => {
    try {
      return !!localStorage.getItem(STORAGE_KEY);
    } catch {
      return false;
    }
  });

  // Merge live rates with admin overrides from settings
  const rates = useMemo(() => {
    const merged = { ...liveRates };
    if (settings) {
      if (Number(settings.eur_rate_override) > 0) merged.EUR = Number(settings.eur_rate_override);
      if (Number(settings.usd_rate_override) > 0) merged.USD = Number(settings.usd_rate_override);
      if (Number(settings.cad_rate_override) > 0) merged.CAD = Number(settings.cad_rate_override);
      if (Number(settings.ngn_rate_override) > 0) merged.NGN = Number(settings.ngn_rate_override);
    }
    return merged;
  }, [liveRates, settings]);

  // Load live exchange rates
  useEffect(() => {
    let alive = true;
    fetchRates().then((r) => {
      if (alive && r) setLiveRates(r);
    });
    return () => {
      alive = false;
    };
  }, []);

  // Auto-detect currency from IP if user hasn't manually picked one
  useEffect(() => {
    if (manuallySet) return;
    let alive = true;
    detectCurrency().then((code) => {
      if (alive && code && CURRENCIES.some((c) => c.code === code)) {
        setCurrencyState(code);
      }
    });
    return () => {
      alive = false;
    };
  }, [manuallySet]);

  const setCurrency = useCallback((code) => {
    if (!CURRENCIES.some((c) => c.code === code)) return;
    setCurrencyState(code);
    setManuallySet(true);
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {
      // localStorage may be unavailable
    }
  }, []);

  const formatPrice = useCallback(
    (priceGbpString) => formatGBPInCurrency(priceGbpString, currency, rates),
    [currency, rates]
  );

  const convertAmount = useCallback(
    (gbpAmount) => convertAndFormat(gbpAmount, currency, rates),
    [currency, rates]
  );

  const getGbpNumber = useCallback((priceGbpString) => {
    const p = parseGBP(priceGbpString);
    return p ? p.amount : null;
  }, []);

  return (
    <CurrencyContext.Provider
      value={{ currency, rates, setCurrency, formatPrice, convertAmount, getGbpNumber }}
    >
      {children}
    </CurrencyContext.Provider>
  );
}

export const useCurrency = () => useContext(CurrencyContext);
